import React, { useState, useLayoutEffect } from 'react';
import { handleResponseLanguage } from '../../../../../../../../i18n';
import InputSelector from '../../../../input/InputSelector';
import {
  Grid,
  Box,
  Flex,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from '@wegroup/design-system';
import { getInputConfig } from '../../../../input/inputConfig';
import useInputGridHeightDifference from '../../hooks/useInputGridHeightDifference';
import useInputGridProps from '../../hooks/useInputGridProps';
import useInputGridConfig from '../../../common/hooks/useInputGridConfig';
import TooltipCircle from '../../../../../../../common/src/components/TooltipCircle';
import useCurrentGroup from '../../../../../hooks/useCurrentGroup';
import { useFlowsRouteParams } from '../../../../../hooks/useFlowsRouteParams';
import FlowsQuestionText from '../FlowsQuestionText';
import { Inputs } from '../../../../../types/Enums';

const QuestionGroupWrapper: React.FC<React.PropsWithChildren<unknown>> = () => {
  const currentGroup = useCurrentGroup();
  const { isExpandedGrid, isFullWidth } = useInputGridConfig();
  const { flowId } = useFlowsRouteParams();

  const [isColumnsEnabled, setIsColumnsEnabled] = useState(
    currentGroup.questions.length > 3,
  );

  const extraGridProps = useInputGridProps({
    isExpandedGrid,
    isFullWidth,
    isColumnsEnabled,
  });

  const { inputRefs, wrapperRef, setGridForHeight } =
    useInputGridHeightDifference();

  useLayoutEffect(() => {
    setIsColumnsEnabled(
      setGridForHeight() ? false : currentGroup.questions.length > 3,
    );
  }, []);

  return (
    <Grid
      gridGap={{ base: '3', tablet: '6' }}
      justifyContent="center"
      mx="auto"
      ref={wrapperRef}
      sx={extraGridProps}
      data-test-id="ProdFac_QuestionGroupWrapper_Grid"
    >
      {currentGroup.questions.map((question, index) => {
        const { styling, config } =
          getInputConfig(flowId || '')[question.rules.input_type] || {};
        return (
          <Box
            key={`${question.id}${question.index ? question.index : ''}`}
            sx={styling}
            ref={(element: HTMLDivElement) =>
              (inputRefs.current[index] = element)
            }
            // This will make sure that nothing is rendered further for the HIDDEN input type
            display={question.rules.input_type === Inputs.HIDDEN ? 'none' : ''}
          >
            {/* TITLE */}
            {!config?.isTitleHidden && (
              <Flex justifyContent="space-between" mb="2">
                <FlowsQuestionText question={question} />
                {question.description && (
                  <Box ml="2">
                    <Popover trigger="hover" placement="auto">
                      <PopoverTrigger>
                        <TooltipCircle size="sm">?</TooltipCircle>
                      </PopoverTrigger>
                      <PopoverContent
                        bg="gray.700"
                        color="white"
                        textStyle="smallBodyMedium"
                        lineHeight="5"
                        sx={{
                          ul: { listStyle: 'disc', marginLeft: 4 },
                          li: { py: 1 },
                        }}
                      >
                        <PopoverArrow bg="gray.700" color="white" />
                        <PopoverBody
                          dangerouslySetInnerHTML={{
                            __html: handleResponseLanguage(
                              question.description,
                            ),
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </Box>
                )}
              </Flex>
            )}
            {/* INPUT */}
            <InputSelector
              key={'group_' + currentGroup.id}
              question={question}
            />
          </Box>
        );
      })}
    </Grid>
  );
};

export default QuestionGroupWrapper;
